/**
 * Lightbox — shared full-screen image preview with prev/next navigation
 * and pinch-to-zoom + pan + wheel-zoom + double-tap-to-toggle.
 *
 * Props:
 *   images:        Array<{ src: string, name?: string, id?: string|number }>
 *   index:         number              — current image index
 *   onIndexChange: (newIndex) => void  — called by prev/next + arrow keys
 *   onClose:       () => void          — called by close btn, click-outside, ESC
 *
 * Keyboard:
 *   Esc         → close
 *   ← / →       → prev / next image (when not zoomed)
 *   + / -       → zoom in / out
 *   0           → reset zoom
 *
 * Mouse:
 *   wheel       → zoom in/out around the cursor
 *   double-click→ toggle between 1× and 2.5× (centered)
 *   drag        → pan (when zoomed in)
 *
 * Touch:
 *   pinch       → zoom (two-finger spread/squeeze)
 *   double-tap  → toggle 1× / 2.5×
 *   one-finger drag → pan (when zoomed in)
 *
 * Used in: BoardDetail.jsx (card attachments), JobsEnhanced.jsx (job photos).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_SCALE = 2.5;
const WHEEL_STEP = 0.2;

export default function Lightbox({ images, index, onIndexChange, onClose }) {
  const total = images?.length || 0;
  const cur = total > 0 ? images[index] : null;

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Image load lifecycle — a slow or failed full-size load used to render a
  // pitch-black overlay with no feedback ("clicked the thumbnail, no picture").
  const [loadState, setLoadState] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const [retryKey, setRetryKey] = useState(0);
  const containerRef = useRef(null);

  // Pinch tracking
  const pinchRef = useRef({ active: false, startDist: 0, startScale: 1 });
  // Pan tracking (single-pointer drag when zoomed)
  const panRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  // Double-tap tracking
  const lastTapRef = useRef(0);

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Reset zoom + load state whenever the displayed image changes
  useEffect(() => { reset(); setLoadState('loading'); setRetryKey(0); }, [index, reset]);

  const next = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  const prev = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && scale <= 1) next();
      else if (e.key === 'ArrowLeft' && scale <= 1) prev();
      else if (e.key === '+' || e.key === '=') setScale((s) => Math.min(MAX_SCALE, s + WHEEL_STEP));
      else if (e.key === '-' || e.key === '_') setScale((s) => Math.max(MIN_SCALE, s - WHEEL_STEP));
      else if (e.key === '0') reset();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [next, prev, onClose, reset, scale]);

  // Wheel zoom
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -WHEEL_STEP : WHEEL_STEP;
    setScale((s) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s + delta));
      // If we're zooming back to 1, snap offset to origin
      if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // Double-click toggle (desktop)
  const onDoubleClick = useCallback((e) => {
    e.stopPropagation();
    setScale((s) => (s > 1 ? 1 : DOUBLE_TAP_SCALE));
    setOffset({ x: 0, y: 0 });
  }, []);

  // ── Touch handlers ────────────────────────────────────────────────
  const dist = (t1, t2) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch start
      pinchRef.current = {
        active: true,
        startDist: dist(e.touches[0], e.touches[1]),
        startScale: scale,
      };
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        // Double-tap → toggle zoom
        e.preventDefault();
        setScale((s) => (s > 1 ? 1 : DOUBLE_TAP_SCALE));
        setOffset({ x: 0, y: 0 });
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
      // Pan start (only meaningful when zoomed in)
      if (scale > 1) {
        panRef.current = {
          active: true,
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          baseX: offset.x,
          baseY: offset.y,
        };
      }
    }
  }, [scale, offset]);

  const onTouchMove = useCallback((e) => {
    if (pinchRef.current.active && e.touches.length === 2) {
      e.preventDefault();
      const d = dist(e.touches[0], e.touches[1]);
      const ratio = d / pinchRef.current.startDist;
      const next = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, pinchRef.current.startScale * ratio)
      );
      setScale(next);
      if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
    } else if (panRef.current.active && e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - panRef.current.startX;
      const dy = e.touches[0].clientY - panRef.current.startY;
      setOffset({ x: panRef.current.baseX + dx, y: panRef.current.baseY + dy });
    }
  }, [scale]);

  const onTouchEnd = useCallback(() => {
    pinchRef.current.active = false;
    panRef.current.active = false;
  }, []);

  // ── Mouse pan (desktop, when zoomed) ───────────────────────────────
  const onMouseDown = useCallback((e) => {
    if (scale <= 1) return;
    e.preventDefault();
    panRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
  }, [scale, offset]);

  const onMouseMove = useCallback((e) => {
    if (!panRef.current.active) return;
    setOffset({
      x: panRef.current.baseX + (e.clientX - panRef.current.startX),
      y: panRef.current.baseY + (e.clientY - panRef.current.startY),
    });
  }, []);

  const onMouseUp = useCallback(() => {
    panRef.current.active = false;
  }, []);

  // Attach a non-passive wheel listener (React's onWheel is passive, so
  // preventDefault is ignored — we need preventDefault to stop the page
  // from scrolling behind the lightbox).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const handler = (e) => onWheel(e);
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [onWheel]);

  if (!cur) return null;

  const isZoomed = scale > 1;
  const cursor = isZoomed ? (panRef.current.active ? 'grabbing' : 'grab') : 'zoom-in';

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Image preview: ${cur.name || ''}`}
      className="fixed inset-0 z-[100] flex items-center justify-center select-none"
      style={{ background: 'rgba(0,0,0,0.92)' }}
      onClick={onClose}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      data-testid="lightbox-overlay"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
        style={{ color: 'white', background: 'rgba(255,255,255,0.08)' }}
        aria-label="Close preview"
        data-testid="lightbox-close-btn"
      >
        <X className="w-5 h-5" />
      </button>

      <div
        className="absolute top-4 left-4 right-20 px-3 py-1.5 rounded text-sm truncate"
        style={{ background: 'rgba(0,0,0,0.4)', color: 'white' }}
        data-testid="lightbox-caption"
      >
        {cur.name || 'Image'}
        {total > 1 && (
          <span className="ml-2 text-xs opacity-70">{index + 1} / {total}</span>
        )}
        {isZoomed && (
          <span className="ml-2 text-xs opacity-70">· {scale.toFixed(1)}×</span>
        )}
      </div>

      {/* Zoom controls */}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1 rounded-full"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-zoom-controls"
      >
        <button
          onClick={() => setScale((s) => Math.max(MIN_SCALE, s - WHEEL_STEP))}
          disabled={scale <= MIN_SCALE}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-30"
          style={{ color: 'white' }}
          aria-label="Zoom out"
          data-testid="lightbox-zoom-out-btn"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <button
          onClick={reset}
          disabled={!isZoomed}
          className="px-3 h-9 rounded-full flex items-center gap-1.5 text-xs hover:bg-white/10 transition-colors disabled:opacity-30"
          style={{ color: 'white' }}
          aria-label="Reset zoom"
          data-testid="lightbox-zoom-reset-btn"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          {scale.toFixed(1)}×
        </button>
        <button
          onClick={() => setScale((s) => Math.min(MAX_SCALE, s + WHEEL_STEP))}
          disabled={scale >= MAX_SCALE}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-30"
          style={{ color: 'white' }}
          aria-label="Zoom in"
          data-testid="lightbox-zoom-in-btn"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
      </div>

      {total > 1 && !isZoomed && (
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
          style={{ color: 'white', background: 'rgba(255,255,255,0.08)' }}
          aria-label="Previous image"
          data-testid="lightbox-prev-btn"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      {loadState === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="lightbox-loading">
          <div className="w-10 h-10 rounded-full border-2 border-white/25 border-t-white animate-spin" />
        </div>
      )}

      {loadState === 'error' ? (
        <div
          className="flex flex-col items-center gap-3 text-center px-6 max-w-sm"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-error"
        >
          <p className="text-white text-sm leading-relaxed">
            This photo couldn&apos;t be loaded. It may be a weak connection — try again.
          </p>
          <button
            type="button"
            onClick={() => { setLoadState('loading'); setRetryKey((k) => k + 1); }}
            className="px-5 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90 transition-opacity"
            style={{ background: '#1E88E5' }}
            data-testid="lightbox-retry-btn"
          >
            Try again
          </button>
        </div>
      ) : (
        <img
          key={retryKey}
          src={retryKey === 0 ? cur.src : `${cur.src}${cur.src.includes('?') ? '&' : '?'}_r=${retryKey}`}
          alt={cur.name || 'Image'}
          draggable={false}
          className="max-w-[92vw] max-h-[88vh] object-contain"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            transition: panRef.current.active || pinchRef.current.active ? 'none' : 'transform 0.15s ease-out',
            cursor,
            touchAction: 'none',
            opacity: loadState === 'loaded' ? 1 : 0,
          }}
          onLoad={() => setLoadState('loaded')}
          onError={() => setLoadState('error')}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={onDoubleClick}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          data-testid="lightbox-image"
        />
      )}

      {total > 1 && !isZoomed && (
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
          style={{ color: 'white', background: 'rgba(255,255,255,0.08)' }}
          aria-label="Next image"
          data-testid="lightbox-next-btn"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
